import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import sentencesData from '../data/typing-sentences.json';

interface TypingAnimationProps {
  className?: string;
}

const TypingAnimation: React.FC<TypingAnimationProps> = ({ className }) => {
  const theme = useTheme();
  
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const sentences = sentencesData.sentences;

  // Get glow colors based on theme mode
  const getGlowColors = () => {
    if (theme.palette.mode === 'dark') {
      return {
        primary: theme.palette.primary.light || '#93c5fd',
        secondary: theme.palette.secondary.light || '#c4b5fd',
        intensity: '80', // Higher intensity for dark mode
      };
    } else {
      return {
        primary: theme.palette.primary.main,
        secondary: theme.palette.secondary.main,
        intensity: '40', // Lower intensity for light mode
      };
    }
  };

  const glowColors = getGlowColors();

  // Initialize with first character to prevent blank space
  useEffect(() => {
    if (!isLoaded && sentences.length > 0) {
      setCurrentText(sentences[0].charAt(0));
      setIsLoaded(true);
    }
  }, [sentences, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;

    const currentSentence = sentences[currentSentenceIndex];
    
    if (isTyping) {
      if (currentText.length < currentSentence.length) {
        const timer = setTimeout(() => {
          setCurrentText(currentSentence.slice(0, currentText.length + 1));
        }, 80); // Faster typing speed
        return () => clearTimeout(timer);
      } else {
        // Finished typing, wait before deleting
        const timer = setTimeout(() => {
          setIsTyping(false);
        }, 1500); // Shorter pause at end
        return () => clearTimeout(timer);
      }
    } else {
      // Deleting
      if (currentText.length > 0) {
        const timer = setTimeout(() => {
          setCurrentText(currentText.slice(0, -1));
        }, 40); // Faster deletion speed
        return () => clearTimeout(timer);
      } else {
        // Finished deleting, move to next sentence
        setCurrentSentenceIndex((prev) => (prev + 1) % sentences.length);
        setIsTyping(true);
      }
    }
  }, [currentText, isTyping, currentSentenceIndex, sentences, isLoaded]);

  // Cursor blink effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorTimer);
  }, []);

  // Convert \n to <br> for line breaks
  const formatText = (text: string) => {
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: '60px', sm: '70px', md: '80px' },
        position: 'relative',
        width: '100%',
        maxWidth: '800px',
        mx: 'auto',
        px: { xs: 1, md: 0 },
        opacity: isLoaded ? 1 : 0.8, // Slight fade-in effect
        transition: 'opacity 0.3s ease',
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontFamily: '"Kievit", "Roboto", sans-serif',
          fontWeight: 600,
          color: 'text.primary',
          textAlign: 'center',
          position: 'relative',
          textShadow: `0 0 10px ${glowColors.primary}${glowColors.intensity}, 0 0 20px ${glowColors.primary}60, 0 0 30px ${glowColors.primary}40`,
          animation: 'glow 2s ease-in-out infinite alternate',
          fontSize: { 
            xs: '1rem', 
            sm: '1.25rem', 
            md: '1.5rem', 
            lg: '1.75rem' 
          },
          lineHeight: { xs: 1.3, md: 1.4 },
          '&::after': {
            content: '""',
            position: 'absolute',
            right: { xs: '-3px', md: '-4px' },
            top: '50%',
            transform: 'translateY(-50%)',
            width: { xs: '1.5px', md: '2px' },
            height: { xs: '1em', md: '1.2em' },
            backgroundColor: glowColors.primary,
            boxShadow: `0 0 8px ${glowColors.primary}, 0 0 16px ${glowColors.primary}90`,
            animation: showCursor ? 'blink 1s infinite, glow 2s ease-in-out infinite alternate' : 'none',
            '@keyframes blink': {
              '0%, 50%': {
                opacity: 1,
              },
              '51%, 100%': {
                opacity: 0,
              },
            },
          },
          '@keyframes glow': {
            '0%': {
              textShadow: `0 0 10px ${glowColors.primary}${glowColors.intensity}, 0 0 20px ${glowColors.primary}60, 0 0 30px ${glowColors.primary}40`,
            },
            '100%': {
              textShadow: `0 0 20px ${glowColors.primary}90, 0 0 30px ${glowColors.primary}70, 0 0 40px ${glowColors.primary}60, 0 0 50px ${glowColors.primary}50`,
            },
          },
        }}
      >
        {formatText(currentText)}
      </Typography>
    </Box>
  );
};

export default TypingAnimation; 